import * as React from 'react';
import type { JSX } from 'react';
import { Modal, ModalTitle, Spinner } from '@cian/ui-kit';

import { SUGGEST_PROP_DEFAULTS } from '../../internal/constants';
import { SuggestModalMobile } from '../../internal/components/SuggestModalMobile/SuggestModalMobile';
import { SuggestionMobileDefaultItem } from '../../internal/components/SuggestionMobileDefaultItem/SuggestionMobileDefaultItem';
import { SuggestEmptyListContent } from '../../internal/components/SuggestEmptyListContent/SuggestEmptyListContent';
import { SuggestionTextItem } from '../../internal/components/SuggestionTextItem/SuggestionTextItem';
import { isEmptyListPlainText } from '../../internal/utils/isEmptyListPlainText';
import { SuggestionTitleItem } from '../../internal/components/SuggestionTitleItem/SuggestionTitleItem';
import { TextHighlighter } from '../../internal/components/TextHighlighter/TextHighlighter';
import { useIconMargin } from '../../internal/hooks/useIconMargin';
import { useMaxLinesCssVarable } from '../../internal/hooks/useMaxLinesCssVarable';
import { useSuggestMouseEvents } from '../../internal/hooks/useSuggestMouseEvents';
import { useSuggestSelectedItem } from '../../internal/hooks/useSuggestSelectedItem';
import { ISuggestionItem, ISuggestMobileProps, TSuggestionItemType } from '../../types';

import { REGION_SUGGEST_MOBILE_MODAL_TITLE, REGION_SUGGEST_MOBILE_MODAL_TOP_OFFSET_PX } from './constants';
import styles from './styles.module.css';

const suggestItemComponentMap: Record<
  TSuggestionItemType,
  React.FC<React.ComponentProps<typeof SuggestionTitleItem | typeof SuggestionMobileDefaultItem>>
> = { default: SuggestionMobileDefaultItem, title: SuggestionTitleItem };

const suggestModalSheetHeight = `calc(100dvh - ${REGION_SUGGEST_MOBILE_MODAL_TOP_OFFSET_PX}px)`;

const modalContentStyle: React.CSSProperties = {
  marginTop: REGION_SUGGEST_MOBILE_MODAL_TOP_OFFSET_PX,
  boxSizing: 'border-box',
  height: suggestModalSheetHeight,
  minHeight: suggestModalSheetHeight,
  maxHeight: suggestModalSheetHeight,
};

/** Мобильный саджест: `Modal` из `@cian/ui-kit`, заголовок в шапке, поле ввода и список в теле модалки. */
export const SuggestMobile = <T,>(props: ISuggestMobileProps<T>): JSX.Element => {
  const { onSelect, onClose } = props;
  const inputSectionRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const emptyListContent = props.emptyListContent;
  const hasEmptyListContent = Boolean(emptyListContent);
  const emptyListPlainText = hasEmptyListContent && isEmptyListPlainText(emptyListContent);
  const emptyListCustomContent = hasEmptyListContent && !isEmptyListPlainText(emptyListContent);
  const isSuggestionsEmpty = props.suggestions.length === 0;
  const isEmptyListLoadingShown = isSuggestionsEmpty && Boolean(props.emptyListLoading);

  const isSuggestModalRendered =
    props.open &&
    (props.suggestions.length > 0 ||
      isEmptyListLoadingShown ||
      (isSuggestionsEmpty && hasEmptyListContent) ||
      Boolean(props.showEmptySuggest));

  useMaxLinesCssVarable({ ref: listRef, maxLines: props.maxLines, open: props.open });
  const { getIconMarginForItem, setTitleItemRefByIndex } = useIconMargin(props.suggestions);

  const onItemClick = React.useCallback(
    (item: ISuggestionItem<T>) => () => onSelect?.(item, { source: 'click' }),
    [onSelect],
  );
  const onModalClose = React.useCallback(() => onClose?.({ source: 'mobileModal' }), [onClose]);
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onModalClose();
      }
    },
    [onModalClose],
  );

  const handleOverlayPointerDownCapture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const overlayEl = event.currentTarget;
      const dialogEl = overlayEl.querySelector<HTMLElement>('[role="dialog"]');
      const target = event.target as Node | null;

      if (!dialogEl || !target || dialogEl.contains(target)) {
        return;
      }

      onModalClose();
    },
    [onModalClose],
  );

  const suggestModalOverlayProps = React.useMemo(
    () => ({ onPointerDownCapture: handleOverlayPointerDownCapture }),
    [handleOverlayPointerDownCapture],
  );

  const focusModalInput = React.useCallback(() => {
    inputSectionRef.current?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  }, []);

  React.useLayoutEffect(() => {
    if (!props.open || !isSuggestModalRendered) {
      return;
    }

    let cancelled = false;
    const tryFocus = () => {
      if (!cancelled) {
        focusModalInput();
      }
    };

    tryFocus();
    const rafId = requestAnimationFrame(tryFocus);
    const timeout0 = window.setTimeout(tryFocus, 0);
    const timeoutDelayed = window.setTimeout(tryFocus, 200);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeout0);
      window.clearTimeout(timeoutDelayed);
    };
  }, [props.open, isSuggestModalRendered, focusModalInput]);

  const { setSelectedItemIndex, selectedItemIndex } = useSuggestSelectedItem(props, {
    suggestionsChildrenRef: inputSectionRef,
    listBoxRef: listRef,
  });
  const mouseEventProps = useSuggestMouseEvents(setSelectedItemIndex);

  const modalHeader = <ModalTitle>{REGION_SUGGEST_MOBILE_MODAL_TITLE}</ModalTitle>;

  return (
    <>
      <div
        className={isSuggestModalRendered ? styles['formFieldPlaceholder'] : undefined}
        aria-hidden={isSuggestModalRendered}
      >
        {!isSuggestModalRendered ? (props.children as JSX.Element) : null}
      </div>
      {isSuggestModalRendered && (
        <Modal
          open={props.open}
          onOpenChange={handleOpenChange}
          preventOutside={false}
          overlay="blured"
          overlayProps={suggestModalOverlayProps}
          width="100%"
          className={styles['modalContent']}
          style={modalContentStyle}
          header={modalHeader}
        >
          <div className={styles['modalBody']}>
            <div ref={inputSectionRef} className={styles['inputWrap']} data-testid="suggest-modal-mobile-input-wrap">
              {props.children}
            </div>
            <div className={styles['listScroll']}>
              {isEmptyListLoadingShown && (
                <SuggestEmptyListContent>
                  <div className={styles['loadingWrap']}>
                    <Spinner size={24} />
                  </div>
                </SuggestEmptyListContent>
              )}
              {isSuggestionsEmpty && !props.emptyListLoading && emptyListCustomContent && (
                <SuggestEmptyListContent>{emptyListContent}</SuggestEmptyListContent>
              )}
              {(props.suggestions.length > 0 ||
                (isSuggestionsEmpty && !props.emptyListLoading && emptyListPlainText)) && (
                <SuggestModalMobile ref={listRef}>
                  {isSuggestionsEmpty && !props.emptyListLoading && emptyListPlainText && (
                    <SuggestionTextItem>{emptyListContent}</SuggestionTextItem>
                  )}
                  {props.suggestions.map((suggestion, i) => {
                    const ItemComponent = suggestItemComponentMap[suggestion.type ?? SUGGEST_PROP_DEFAULTS.itemType];

                    return (
                      <ItemComponent
                        {...suggestion}
                        key={i}
                        type={suggestion.type}
                        iconMargin={getIconMarginForItem(suggestion)}
                        ref={suggestion.type === 'title' ? setTitleItemRefByIndex(i) : undefined}
                        onClick={onItemClick(suggestion)}
                        isSelected={selectedItemIndex === i}
                        onMouseMove={mouseEventProps.onMouseMove(i)}
                        onMouseLeave={mouseEventProps.onMouseLeave}
                        onMouseEnter={mouseEventProps.onMouseEnter}
                        variant="mobile"
                      >
                        <TextHighlighter highlightValue={props.highlightValue}>{suggestion.title}</TextHighlighter>
                      </ItemComponent>
                    );
                  })}
                </SuggestModalMobile>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

SuggestMobile.displayName = 'SuggestMobile';
